import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}


class CartDeliveryDate extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="date"]');
    this.error = this.querySelector('.cart-delivery-date__error');

    if (!this.input) return;

    this.setMaxDate();

    this.input.addEventListener('change', () => {
      this.handleChange();
    });
  }

  setMaxDate() {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 1);

    this.input.max = this.formatDate(maxDate);
  }

  async handleChange() {
    const value = this.input.value;

    if (!value) {
      this.clearError();
      await this.save('');
      return;
    }

    const error = this.getValidationError(value);

    if (error) {
      this.input.value = '';
      this.showError(error);
      await this.save('');
      return;
    }

    this.clearError();
    await this.save(value);
  }

  getValidationError(value) {
    const selected = new Date(`${value}T00:00:00`);
    const min = this.input.min ? new Date(`${this.input.min}T00:00:00`) : null;
    const max = this.input.max ? new Date(`${this.input.max}T00:00:00`) : null;
    const day = selected.getDay();

    if (day === 0 || day === 6) {
      return 'Weekend delivery is not available. Please select a weekday.';
    }

    if (min && selected < min) {
      return 'Please select a date from tomorrow onwards.';
    }

    if (max && selected > max) {
      return 'Please select a date within the next 30 days.';
    }

    return null;
  }

  async save(value) {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          'Needed by date': value
        }
      })
    });
  }

  showError(message) {
    if (!this.error) return;

    this.error.textContent = message;
    this.error.hidden = false;
  }

  clearError() {
    if (!this.error) return;

    this.error.textContent = '';
    this.error.hidden = true;
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}

if (!customElements.get('cart-delivery-date')) {
  customElements.define('cart-delivery-date', CartDeliveryDate);
}